// use-crm-permissions — RBAC do CRM no frontend (Story 11.13 / FR-59.2).
//
// Fonte ÚNICA das permissões CRM na UI: consolida o `WRITE_ROLES` que estava
// duplicado em 7 telas (leads/deals/proposals × index/detalhe + kanban). O backend
// valida sempre (RolesGuard) — aqui é só UX: esconde ações que o usuário não pode
// executar. Espelha a matriz FR-59:
//   - escrita CRUD (lead/deal/proposta/atividade) + ações: admin, comercial;
//   - leitura: admin, comercial, financeiro, suporte (dev não acessa dados CRM);
//   - converter proposta → assinatura: admin, comercial (mesma regra de escrita).

import { useMemo } from 'react';

import { useAuth } from './use-auth';
import type { AuthUser } from '../stores/useAuthStore';

type Role = AuthUser['role'];

/** Roles que escrevem no CRM (CRUD + ações) — espelha @Roles('admin','comercial'). */
const WRITE_ROLES: ReadonlySet<Role> = new Set<Role>(['admin', 'comercial']);
/** Roles que leem dados CRM — espelha os GETs read-only (+ financeiro/suporte). */
const READ_ROLES: ReadonlySet<Role> = new Set<Role>([
  'admin',
  'comercial',
  'financeiro',
  'suporte',
]);

export interface CrmPermissions {
  /** Pode criar/editar leads, deals, propostas e atividades + mover estágio. */
  canWrite: boolean;
  /** Pode converter proposta aceita em assinatura (= canWrite na matriz FR-59). */
  canConvert: boolean;
  /** Pode ler dados CRM (lista/detalhe). `dev` não lê dados estratégicos. */
  canReadCrm: boolean;
  /** Role atual (ou null se não autenticado) — pra checagens pontuais. */
  role: Role | null;
}

/**
 * Permissões CRM derivadas da role do usuário logado. Memoizado pela role pra não
 * recriar objeto a cada render. Quando não há usuário, tudo é `false` (UI conservadora).
 */
export function useCrmPermissions(): CrmPermissions {
  const { user } = useAuth();
  const role = user?.role ?? null;

  return useMemo<CrmPermissions>(() => {
    const canWrite = role !== null && WRITE_ROLES.has(role);
    return {
      canWrite,
      canConvert: canWrite,
      canReadCrm: role !== null && READ_ROLES.has(role),
      role,
    };
  }, [role]);
}
